import { Alert, Autocomplete, Box, Button, Chip, TextField, Typography } from '@mui/material';
import type { ProviderLicenseType } from '@store/generated/organizations';
import {
  PROVIDER_LICENSE_TYPE_OPTIONS,
  type ProviderLicenseTypeOption,
} from '@utils/providerLicenseTypes';

import type { GeneratedIntakeFormRequest, GeneratedIntakeGroupHelp } from '../../types';

import { GroupHelpTrigger, HelpPanelDrawer } from './HelpPanelDrawer';
import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { cardSx, chipSx, greyCardSx, groupTitleSx, wf } from './tokens';
import type { PayerRow } from './types';
import { useGroupHelpPanel } from './useGroupHelpPanel';

export interface EligibleProvider {
  userId: string;
  name: string;
}

export interface IntakeConfig {
  role: GeneratedIntakeFormRequest['role'];
  products: GeneratedIntakeFormRequest['products'];
  isPrescriber: boolean;
  licenseTypes: ProviderLicenseType[];
  practiceType: NonNullable<GeneratedIntakeFormRequest['practice_type']>;
  payerKeys: string[];
}

interface IntakeConfigPageProps {
  config: IntakeConfig;
  onChange: (next: Partial<IntakeConfig>) => void;
  payerOptions: { payer_key?: string; display_name?: string }[];
  payerRows: PayerRow[];
  eligibleProviders?: EligibleProvider[];
  eligibleProvidersLoading?: boolean;
  eligibleProvidersError?: boolean;
  rosterCandidates?: EligibleProvider[];
  canManageRoster?: boolean;
  onAddRosterProvider?: (userId: string) => void;
  onRemoveRosterProvider?: (userId: string) => void;
  derivedSentence: string | null;
  onContinue: () => void;
  groups?: GeneratedIntakeGroupHelp[];
  onHelpAction?: (action: 'message_specialist') => void;
  readOnlyReason?: string;
  saveLabel?: string;
  onSave?: () => void;
  saveDisabled?: boolean;
  saveError?: boolean;
}

const ROLES: { value: IntakeConfig['role']; label: string }[] = [
  { value: 'clinic_manager', label: 'Clinic manager' },
  { value: 'provider', label: 'Provider' },
  { value: 'manager_who_also_practices', label: 'Manager who also sees patients' },
];

const PRODUCT_LABELS: Record<IntakeConfig['products'][number], string> = {
  jotbill: 'JotBill (billing)',
  jotcred: 'JotCred (credentialing)',
};

function Radio({
  selected,
  label,
  onClick,
  disabled = false,
}: {
  selected: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onClick}
      onKeyDown={event => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        padding: '4px 0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      <Box
        component="span"
        sx={{
          width: '13px',
          height: '13px',
          borderRadius: '50%',
          border: `1.5px solid ${selected ? wf.blue : wf.faint}`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && (
          <Box
            component="span"
            sx={{ width: '7px', height: '7px', borderRadius: '50%', background: wf.blue }}
          />
        )}
      </Box>
      {label}
    </Box>
  );
}

function GroupCard({
  title,
  chip,
  help,
  children,
  grey,
  full,
}: {
  title: string;
  chip?: string;
  help?: React.ReactNode;
  children: React.ReactNode;
  grey?: boolean;
  full?: boolean;
}) {
  return (
    <Box sx={{ ...(grey ? greyCardSx : cardSx), ...(full ? { gridColumn: '1 / -1' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <Box sx={groupTitleSx}>{title}</Box>
        {help}
        {chip && <Box sx={chipSx('mut')}>{chip}</Box>}
      </Box>
      {children}
    </Box>
  );
}

export function IntakeConfigPage({
  config,
  onChange,
  payerOptions,
  payerRows,
  eligibleProviders = [],
  eligibleProvidersLoading = false,
  eligibleProvidersError = false,
  rosterCandidates = [],
  canManageRoster = false,
  onAddRosterProvider,
  onRemoveRosterProvider,
  derivedSentence,
  onContinue,
  groups = [],
  onHelpAction = () => undefined,
  readOnlyReason,
  saveLabel,
  onSave,
  saveDisabled,
  saveError = false,
}: IntakeConfigPageProps) {
  const { selectedGroup, selectedGroupId, openGroup, closeGroup } = useGroupHelpPanel(groups);
  const drawerId = 'intake-config-group-help';
  const helpFor = (groupId: string, fallbackName: string) => {
    const group = groups.find(entry => entry.group_id === groupId);
    if (!group?.help_panel) return null;
    return (
      <GroupHelpTrigger
        groupName={group.group_name || fallbackName}
        controlsId={drawerId}
        expanded={selectedGroupId === groupId}
        onOpen={opener => openGroup(groupId, opener)}
      />
    );
  };
  const togglePayer = (payerKey: string) =>
    onChange({
      payerKeys: config.payerKeys.includes(payerKey)
        ? config.payerKeys.filter(item => item !== payerKey)
        : [...config.payerKeys, payerKey],
    });
  const showsProviderRosterHandoff = config.practiceType === 'group' && config.role !== 'provider';

  return (
    <>
      <IntakePageHeader
        title="A few questions first"
        sub="Your answers decide exactly what we ask for — nothing else is shown."
      />

      {readOnlyReason && <Alert severity="info">{readOnlyReason}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
        <GroupCard title="Your role" help={helpFor('C01', 'Your role')}>
          {ROLES.map(role => (
            <Radio
              key={role.value}
              selected={config.role === role.value}
              label={role.label}
              onClick={() => onChange({ role: role.value })}
              disabled={Boolean(readOnlyReason)}
            />
          ))}
        </GroupCard>

        <GroupCard
          title="What we're setting up"
          chip="set by your JotPsych team"
          help={helpFor('C02', "What we're setting up")}
        >
          {config.products.length > 0 ? (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {config.products.map(product => (
                <Box
                  component="li"
                  key={product}
                  sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}
                >
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{ color: wf.green, fontSize: '14px', lineHeight: 1 }}
                  >
                    ✓
                  </Box>
                  <Typography sx={{ fontSize: '13px' }}>{PRODUCT_LABELS[product]}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '13px', color: wf.muted }}>
              No services have been assigned to this intake yet. Contact your JotPsych team.
            </Typography>
          )}
        </GroupCard>

        <GroupCard
          title="Do you prescribe medication?"
          help={helpFor('C03', 'Do you prescribe medication?')}
        >
          <Radio
            selected={config.isPrescriber}
            label="Yes"
            onClick={() => onChange({ isPrescriber: true })}
            disabled={Boolean(readOnlyReason)}
          />
          <Radio
            selected={!config.isPrescriber}
            label="No"
            onClick={() => onChange({ isPrescriber: false })}
            disabled={Boolean(readOnlyReason)}
          />
        </GroupCard>

        <GroupCard
          title="Your license / credential type(s)"
          help={helpFor('C04', 'Your license / credential type(s)')}
        >
          <Autocomplete<ProviderLicenseTypeOption, true>
            disabled={Boolean(readOnlyReason)}
            multiple
            disableCloseOnSelect
            filterSelectedOptions
            options={PROVIDER_LICENSE_TYPE_OPTIONS}
            value={PROVIDER_LICENSE_TYPE_OPTIONS.filter(option =>
              config.licenseTypes.includes(option.value),
            )}
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            onChange={(_event, options) =>
              onChange({ licenseTypes: options.map(option => option.value) })
            }
            renderValue={(selected, getItemProps) =>
              selected.map((option, index) => {
                const { key, ...chipProps } = getItemProps({ index });
                return <Chip key={key} label={option.value} size="small" {...chipProps} />;
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Select credentials"
                placeholder={config.licenseTypes.length === 0 ? 'Search credentials' : undefined}
                size="small"
              />
            )}
          />
        </GroupCard>

        <GroupCard title="Solo or group practice" help={helpFor('C05', 'Solo or group practice')}>
          <Radio
            selected={config.practiceType === 'solo'}
            label="Solo"
            onClick={() => onChange({ practiceType: 'solo' })}
            disabled={Boolean(readOnlyReason)}
          />
          <Radio
            selected={config.practiceType === 'group'}
            label="Group"
            onClick={() => onChange({ practiceType: 'group' })}
            disabled={Boolean(readOnlyReason)}
          />
        </GroupCard>

        {showsProviderRosterHandoff && (
          <GroupCard
            title="Eligible providers"
            chip="managed by your practice"
            help={helpFor('R01', 'Eligible providers')}
            grey
          >
            {eligibleProvidersLoading ? (
              <Typography role="status" sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                Loading providers…
              </Typography>
            ) : eligibleProvidersError ? (
              <Typography role="alert" sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                We couldn&apos;t load this practice&apos;s providers. Refresh and try again.
              </Typography>
            ) : (
              <>
                {eligibleProviders.length === 0 ? (
                  <Typography sx={{ fontSize: '13px', color: wf.muted, margin: 0 }}>
                    No providers added yet. Add the providers who need credentialing or
                    enrollment — practice members who are only trialing SmartScribe are
                    not included automatically.
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {eligibleProviders.map(provider => (
                      <Box
                        component="li"
                        key={provider.userId}
                        sx={{ display: 'flex', alignItems: 'center', gap: '8px', py: '4px' }}
                      >
                        <Box component="span" aria-hidden="true" sx={{ color: wf.green }}>
                          ✓
                        </Box>
                        <Typography sx={{ fontSize: '13px', flexGrow: 1 }}>
                          {provider.name}
                        </Typography>
                        {canManageRoster && onRemoveRosterProvider && !readOnlyReason && (
                          <Button
                            size="small"
                            onClick={() => onRemoveRosterProvider(provider.userId)}
                            sx={{ fontSize: '12px', minWidth: 0, color: wf.muted }}
                          >
                            Remove
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
                {canManageRoster && onAddRosterProvider && !readOnlyReason && (
                  <Autocomplete
                    options={rosterCandidates}
                    getOptionLabel={option => option.name}
                    value={null}
                    blurOnSelect
                    onChange={(_event, option) => {
                      if (option) onAddRosterProvider(option.userId);
                    }}
                    noOptionsText="No more practice members to add"
                    renderInput={params => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Add a provider…"
                        aria-label="Add a provider to the intake roster"
                      />
                    )}
                    sx={{ mt: '8px', maxWidth: '320px' }}
                  />
                )}
              </>
            )}
          </GroupCard>
        )}

        <GroupCard
          title="Your payer list"
          chip="set by your JotPsych team from your agreement — clinic admin can edit"
          help={helpFor('C07', 'Your payer list')}
          full
        >
          <Box
            component="table"
            sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}
          >
            <Box component="thead">
              <Box component="tr">
                {['Insurance company', 'States', 'Plans'].map(heading => (
                  <Box
                    key={heading}
                    component="th"
                    sx={{
                      textAlign: 'left',
                      fontSize: '10.5px',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em',
                      color: wf.muted,
                      padding: '6px 8px',
                      borderBottom: `1px solid ${wf.line}`,
                    }}
                  >
                    {heading}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {payerRows.map(row => (
                <Box component="tr" key={row.payerKey}>
                  <Box
                    component="td"
                    sx={{ padding: '7px 8px', borderBottom: `1px solid ${wf.lineSoft}` }}
                  >
                    {row.displayName}
                  </Box>
                  <Box
                    component="td"
                    sx={{ padding: '7px 8px', borderBottom: `1px solid ${wf.lineSoft}` }}
                  >
                    {row.states}
                  </Box>
                  <Box
                    component="td"
                    sx={{ padding: '7px 8px', borderBottom: `1px solid ${wf.lineSoft}` }}
                  >
                    {row.plans}
                  </Box>
                </Box>
              ))}
              {payerRows.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={3} sx={{ padding: '10px 8px', color: wf.faint }}>
                    No payers selected yet.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
            {payerOptions.map(payer => {
              const key = payer.payer_key ?? '';
              const selected = config.payerKeys.includes(key);
              return (
                <Box
                  key={key}
                  onClick={readOnlyReason ? undefined : () => togglePayer(key)}
                  sx={{
                    ...chipSx(selected ? 'ok' : 'mut'),
                    fontSize: '11.5px',
                    padding: '4px 9px',
                    cursor: readOnlyReason ? 'not-allowed' : 'pointer',
                    opacity: readOnlyReason ? 0.65 : 1,
                  }}
                >
                  {selected ? '✓ ' : '+ '}
                  {payer.display_name}
                </Box>
              );
            })}
          </Box>

          {derivedSentence && (
            <Box
              sx={{
                marginTop: '10px',
                fontSize: '11.5px',
                color: wf.amberWarn,
                background: wf.amberWarnSoft,
                borderRadius: '6px',
                padding: '8px 10px',
              }}
            >
              {derivedSentence}
            </Box>
          )}
        </GroupCard>
      </Box>

      {saveError && (
        <Alert severity="error">Configuration changes could not be saved. Review and retry.</Alert>
      )}

      <IntakePageFooter
        primaryLabel="See what we'll need →"
        onPrimary={onContinue}
        disabled={config.products.length === 0}
        saveLabel={saveLabel}
        onSave={onSave}
        saveDisabled={saveDisabled}
      />
      <HelpPanelDrawer
        drawerId={drawerId}
        panel={selectedGroup?.help_panel ?? null}
        open={Boolean(selectedGroup?.help_panel)}
        onClose={closeGroup}
        onAction={onHelpAction}
      />
    </>
  );
}
